<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FormsAPI from 'dashboard/api/forms';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  formId: { type: [String, Number], default: null },
  kind: {
    type: String,
    required: true,
    validator: v => ['logo', 'header'].includes(v),
  },
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
});

const modelValue = defineModel({ type: String, default: '' });

const { t } = useI18n();

const ACCEPT = 'image/png,image/jpeg,image/svg+xml,image/webp';
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/svg+xml',
  'image/webp',
];
const MAX_SIZE_BYTES = 2 * 1024 * 1024;

const fileInput = ref(null);
const isUploading = ref(false);
const errorMessage = ref('');

const isSaved = computed(() => Boolean(props.formId));

const chooseButtonLabel = computed(() =>
  modelValue.value
    ? t('FORMS_MGMT.EDITOR.APPEARANCE.CHANGE_IMAGE')
    : t('FORMS_MGMT.EDITOR.APPEARANCE.CHOOSE_IMAGE')
);

const openPicker = () => {
  if (!isSaved.value || isUploading.value) return;
  fileInput.value?.click();
};

const removeImage = () => {
  modelValue.value = '';
  errorMessage.value = '';
};

const validateFile = file => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return t('FORMS_MGMT.EDITOR.APPEARANCE.UPLOAD_ERROR_TYPE');
  }
  if (file.size > MAX_SIZE_BYTES) {
    return t('FORMS_MGMT.EDITOR.APPEARANCE.UPLOAD_ERROR_SIZE');
  }
  return '';
};

const onFileChange = async event => {
  const [file] = event.target.files;
  // Allow re-selecting the same file later even if the upload fails.
  event.target.value = '';
  if (!file) return;

  errorMessage.value = validateFile(file);
  if (errorMessage.value) return;

  isUploading.value = true;
  try {
    const { data } = await FormsAPI.uploadImage(props.formId, props.kind, file);
    modelValue.value = data.url;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      t('FORMS_MGMT.EDITOR.APPEARANCE.UPLOAD_ERROR_GENERIC');
  } finally {
    isUploading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <span class="text-sm font-medium text-n-slate-12">{{ label }}</span>

    <div
      v-if="!isSaved"
      class="text-xs text-n-slate-11 border border-dashed border-n-weak rounded-md p-3"
    >
      {{ t('FORMS_MGMT.EDITOR.APPEARANCE.SAVE_FIRST_FOR_IMAGES') }}
    </div>

    <div v-else class="flex items-center gap-3">
      <div
        v-if="modelValue"
        class="flex items-center justify-center w-16 h-16 overflow-hidden border rounded-md shrink-0 border-n-weak bg-n-solid-1"
      >
        <img :src="modelValue" alt="" class="object-contain w-full h-full" />
      </div>

      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPT"
        class="hidden"
        @change="onFileChange"
      />

      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-upload"
          faded
          slate
          xs
          :is-loading="isUploading"
          :label="chooseButtonLabel"
          @click="openPicker"
        />
        <Button
          v-if="modelValue"
          icon="i-lucide-trash-2"
          ruby
          faded
          xs
          :label="t('FORMS_MGMT.EDITOR.APPEARANCE.REMOVE_IMAGE')"
          @click="removeImage"
        />
      </div>
    </div>

    <p v-if="hint && isSaved" class="text-xs text-n-slate-11">{{ hint }}</p>
    <p v-if="errorMessage" class="text-xs text-n-ruby-9">{{ errorMessage }}</p>
  </div>
</template>
